import type { HomeObraEmAndamento, HomeOverview, HomeSummary } from "./home.types";

/**
 * Massa de dados da Tela Inicial (AC4/DR5).
 *
 * Os valores reproduzem os do protótipo do Figma ("Início / Visão Geral" e
 * "Início / Obras em Andamento"), para que a tela possa ser conferida lado a
 * lado com o desenho enquanto o BFF não expõe os indicadores. O arquivo é a
 * única fonte de dados desta entrega e some por completo quando a implementação
 * HTTP do gateway assumir o lugar da de mock.
 */

const OVERVIEW: HomeOverview = {
  capex: {
    anos: 5,
    total: 989.3,
    serie: [
      { label: "2026", value: 265.6 },
      { label: "2027", value: 190.4 },
      { label: "2028", value: 180.7 },
      { label: "2029", value: 151.7 },
      { label: "2030", value: 201.2 },
    ],
  },
  outlook: {
    ano: 2026,
    valor: 265.6,
    variacaoOrcamentoPercentual: -3,
    totalObras: 47,
    obrasExecutadas: 23,
  },
  tipoObra: {
    ano: 2026,
    itens: [
      { sigla: "MC", valor: 45.1, percentual: 17 },
      { sigla: "DE", valor: 69.2, percentual: 26 },
      { sigla: "DI", valor: 11.2, percentual: 4 },
      { sigla: "PP", valor: 128.8, percentual: 48 },
      { sigla: "UP", valor: 11.3, percentual: 4 },
    ],
  },
  escopo: [
    { id: "servicos", label: "Serviços", percentual: 25, valor: 63.9, variacaoPercentual: -2 },
    { id: "materiais", label: "Materiais", percentual: 60, valor: 163.3, variacaoPercentual: -3 },
    {
      id: "facilidades",
      label: "Facilidades",
      percentual: 15,
      valor: 38.5,
      variacaoPercentual: -3,
    },
  ],
  custosPorSubsistema: {
    ano: 2026,
    itens: [
      { codigo: "630.001", nome: "Propulsão", valor: 22 },
      { codigo: "600.001", nome: "Motor Principal de Diesel", valor: 17 },
      { codigo: "610.001", nome: "Geradores principais", valor: 12 },
      { codigo: "651.003", nome: "Sistema de lastro", valor: 9 },
      { codigo: "612.004", nome: "Casco e estrutura", valor: 8 },
      { codigo: "674.001", nome: "Sistema elétrico", valor: 6 },
    ],
  },
};

/**
 * Curva de desembolso do cartão Financeiro. A série é longa de propósito: o
 * gráfico do protótipo é uma área contínua, sem rótulo por ponto, com o marcador
 * "Hoje" perto do fim do período.
 */
function curvaDesembolso(valores: number[]): { label: string; value: number }[] {
  return valores.map((value, index) => ({ label: `S${index + 1}`, value }));
}

const OBRAS: HomeObraEmAndamento[] = [
  {
    id: "160",
    chamada: "A Mobilização do Parcel dos Reis termina no dia 25 de fevereiro.",
    descricao: "Acesse a obra 160 para mais informações.",
    periodo: { inicio: "2026-01-26", termino: "2026-02-25", duracaoDias: 31 },
    financeiro: {
      valorTotal: 30.1,
      fel: "FEL-4",
      legenda: "Valor Total",
      serie: curvaDesembolso([
        4.1, 5.2, 6.8, 8.4, 12.6, 10.9, 9.7, 8.9, 11.4, 14.8, 15.6, 16.2, 21.4, 30.1,
      ]),
      indiceHoje: 11,
      fases: [
        { sigla: "MC", codigo: "100", percentual: 5 },
        { sigla: "ES", codigo: "101", percentual: 5 },
        { sigla: "CO", codigo: "103", percentual: 10 },
        { sigla: "EM", codigo: "105", percentual: 10 },
        { sigla: "RE", codigo: "301", percentual: 70 },
      ],
    },
    progresso: [
      { label: "Materiais Entregues", percentual: 60, realizado: 600, total: 1000 },
      { label: "Serviços Concluídos", percentual: 30, realizado: 30, total: 100 },
      { label: "Consumo de Facilidades", percentual: 55, realizado: 11, total: 20 },
    ],
    gmud: { aprovadas: 56, total: 70, adicao: 32, exclusao: 14, alteracao: 7, quebra: 3 },
  },
  {
    id: "029",
    chamada: "A Docagem do A. Abrolhos entra na fase de reparo estrutural.",
    descricao: "Acesse a obra 029 para mais informações.",
    periodo: { inicio: "2026-02-02", termino: "2026-04-17", duracaoDias: 75 },
    financeiro: {
      valorTotal: 48.7,
      fel: "FEL-3",
      legenda: "Valor Total",
      serie: curvaDesembolso([
        2.4, 3.9, 6.1, 9.5, 13.2, 15.8, 18.4, 21.7, 26.3, 30.8, 35.1, 39.6, 44.2, 48.7,
      ]),
      indiceHoje: 8,
      fases: [
        { sigla: "MC", codigo: "100", percentual: 8 },
        { sigla: "ES", codigo: "101", percentual: 12 },
        { sigla: "CO", codigo: "103", percentual: 15 },
        { sigla: "EM", codigo: "105", percentual: 20 },
        { sigla: "RE", codigo: "301", percentual: 45 },
      ],
    },
    progresso: [
      { label: "Materiais Entregues", percentual: 42, realizado: 336, total: 800 },
      { label: "Serviços Concluídos", percentual: 18, realizado: 27, total: 150 },
      { label: "Consumo de Facilidades", percentual: 35, realizado: 7, total: 20 },
    ],
    gmud: { aprovadas: 31, total: 44, adicao: 18, exclusao: 6, alteracao: 5, quebra: 2 },
  },
  {
    id: "033",
    chamada: "O Upgrade do I. São Sebastião conclui os testes de comissionamento.",
    descricao: "Acesse a obra 033 para mais informações.",
    periodo: { inicio: "2025-11-10", termino: "2026-03-06", duracaoDias: 116 },
    financeiro: {
      valorTotal: 63.4,
      fel: "FEL-4",
      legenda: "Valor Total",
      serie: curvaDesembolso([
        6.2, 11.4, 17.9, 24.1, 28.6, 33.2, 38.9, 43.5, 48.1, 52.7, 56.3, 59.1, 61.8, 63.4,
      ]),
      indiceHoje: 12,
      fases: [
        { sigla: "MC", codigo: "100", percentual: 4 },
        { sigla: "ES", codigo: "101", percentual: 6 },
        { sigla: "CO", codigo: "103", percentual: 12 },
        { sigla: "EM", codigo: "105", percentual: 18 },
        { sigla: "RE", codigo: "301", percentual: 60 },
      ],
    },
    progresso: [
      { label: "Materiais Entregues", percentual: 88, realizado: 1056, total: 1200 },
      { label: "Serviços Concluídos", percentual: 74, realizado: 148, total: 200 },
      { label: "Consumo de Facilidades", percentual: 81, realizado: 26, total: 32 },
    ],
    gmud: { aprovadas: 62, total: 68, adicao: 21, exclusao: 9, alteracao: 11, quebra: 1 },
  },
];

/**
 * Devolve uma cópia da massa de dados a cada chamada, para que a tela nunca
 * altere o mock por referência e cada consulta se comporte como uma resposta
 * nova do servidor.
 */
export function createHomeSummaryMock(): HomeSummary {
  return structuredClone({ overview: OVERVIEW, obrasEmAndamento: { obras: OBRAS } });
}
